import * as React from "react"
import injectSheet from "react-jss"
import cn from "classnames"
import compose from "recompose/compose"
import branch from "recompose/branch"
import renderComponent from "recompose/renderComponent"
import renderNothing from "recompose/renderNothing"
import { color } from "@xingternal/360-styles/helpers"
import { LOADED, LOADING } from "@/helpers/stateMachine"
import { withBenchmarkQuery } from "@/helpers/hoc/withBenchmarkQuery"
import { withLoadingStatus } from "@/helpers/hoc/withLoadingStatus"
import { withDateRange } from "@/helpers/hoc/withDateRange"
import { withKpiScope } from "@/helpers/hoc/withKpiScope"
import { withi18n } from "@/helpers/hoc/withi18n"
import { Text, Tooltip } from "@xingternal/360-components"
import { LoadingText } from "@xingternal/winery-ui/lib/LoadingText"

import { BenchmarkIcon } from "@/components"

const i18nSets = {
  currentUser: {
    benchmarkText: "360_RECRUITER_INSIGHTS_OVERVIEW_TOP_RECRUITERS",
    benchmarkTopNumber: "360_RECRUITER_INSIGHTS_OVERVIEW_YOU_ARE_TOP_RECRUITER",
    benchmarkTooltip: "360_RECRUITER_INSIGHTS_OVERVIEW_TOOLTIP_TOP_RECRUITERS",
  },
  company: {
    benchmarkText: "360_RECRUITER_INSIGHTS_OVERVIEW_TOP_COMPANIES",
    benchmarkTopNumber: "360_RECRUITER_INSIGHTS_COMPANY_WORK_FOR_TOP_COMPANY",
    benchmarkTooltip: "360_RECRUITER_INSIGHTS_OVERVIEW_TOOLTIP_TOP_COMPANIES",
  },
}

const i18n = (scope) => i18nSets[scope]

const styles = {
  benchmark: {
    composes: "flex items-center gray-50",
  },
  benchmarkIcon: { composes: "mr4" },
  benchmarkText: { lineHeight: 1 },
  benchmarkLoading: { composes: "inline-flex items-center" },
  benchmarkLoadingText: { composes: "ml8" },
}

const LoadingBenchmark = injectSheet(styles)(
  ({ benchmarkText, classes, className }) => (
    <div
      data-testid="kpiBenchmarkValue"
      className={cn(classes.benchmark, className)}
    >
      <BenchmarkIcon
        color={color("gray-50")}
        className={classes.benchmarkIcon}
      />
      <Text className={classes.benchmarkText} size={11}>
        <div className={classes.benchmarkLoading}>
          {benchmarkText}{" "}
          <LoadingText className={classes.benchmarkLoadingText} />
        </div>
      </Text>
    </div>
  ),
)

const hideIfFenced = branch(({ fenced }) => fenced, renderNothing)

const renderLoadingWhileWaitingQuery = branch(
  ({ kpiValueStatus }) => kpiValueStatus === LOADED,
  hideIfFenced,
  renderComponent(LoadingBenchmark),
)

const enhance = compose(
  withDateRange,
  withKpiScope,
  branch(({ scope }) => scope === "company", renderNothing),
  withi18n(({ scope }) => i18n(scope)),
  renderLoadingWhileWaitingQuery,
  withBenchmarkQuery,
  withLoadingStatus,
  branch(({ status }) => status === LOADING, renderComponent(LoadingBenchmark)),
  injectSheet(styles),
)

const ComparisonToBenchmark = ({
  classes,
  className,
  benchmarkValue,
  error,
  formatter,
  reverseComparison,
  value,
  visible,
  benchmarkText,
  benchmarkTopNumber,
  benchmarkTooltip,
}) => {
  const returnWithReverse = (op1, op2) => {
    if (!reverseComparison) return value >= benchmarkValue ? op1 : op2
    if (value === null) return op2

    return benchmarkValue >= value ? op1 : op2
  }

  return (
    <div className={className}>
      {visible && benchmarkValue && (
        <Tooltip
          text={
            !error &&
            returnWithReverse(
              `${benchmarkText} ${formatter(benchmarkValue)}`,
              benchmarkTooltip,
            )
          }
          portalContainer={document.body}
          zIndex={5}
        >
          <div data-testid="kpiBenchmarkValue" className={classes.benchmark}>
            <BenchmarkIcon
              className={classes.benchmarkIcon}
              color={returnWithReverse(color("blue"), color("gray-50"))}
            />
            <Text className={classes.benchmarkText} size={11}>
              {returnWithReverse(
                <span>{benchmarkTopNumber}</span>,
                `${benchmarkText} ${formatter(benchmarkValue)}`,
              )}
            </Text>
          </div>
        </Tooltip>
      )}
    </div>
  )
}

ComparisonToBenchmark.defaultProps = {
  classes: {},
}

export const EnhancedComparisonToBenchmark = enhance(ComparisonToBenchmark)
export const StyledComparisonToBenchmark = injectSheet(styles)(
  ComparisonToBenchmark,
)
export default ComparisonToBenchmark
